var _dec, _class, _Overlay;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { testable } from '@instructure/ui-testable';
import { createChainedFunction } from '@instructure/ui-utils';
import { omitProps, pickProps } from '@instructure/ui-react-utils';
import { Dialog } from '@instructure/ui-dialog';
import { Portal } from '@instructure/ui-portal';
import { Transition } from '@instructure/ui-motion';
import { allowedProps, propTypes } from './props';

/**
---
category: components
---
**/
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
let Overlay = (_dec = testable(), _dec(_class = (_Overlay = class Overlay extends Component {
  constructor(props) {
    super(props);
    this._timeouts = [];
    this._DOMNode = null;
    this._isMounted = false;
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handlePortalOpen = DOMNode => {
      this.DOMNode = DOMNode;
      this._timeouts.push(setTimeout(() => {
        if (this._isMounted) {
          this.setState({
            open: true
          });
        }
      }));
    };
    this.handleTransitionExited = _type => {
      this.setState({
        open: false,
        transitioning: false
      });
    };
    this.state = {
      open: props.open,
      transitioning: false
    };
  }
  componentDidMount() {
    this._isMounted = true;
  }
  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      // closing
      this.setState({
        transitioning: !!prevProps.transition
      });
    }
  }
  componentWillUnmount() {
    this._isMounted = false;
    this._timeouts.forEach(timeout => clearTimeout(timeout));
  }
  get DOMNode() {
    return this._DOMNode;
  }
  set DOMNode(el) {
    this._DOMNode = el;
  }
  renderTransition(content) {
    return _jsx(Transition, {
      ...pickProps(this.props, Transition.allowedProps),
      in: this.props.open,
      transitionOnMount: true,
      unmountOnExit: true,
      type: this.props.transition,
      onExited: createChainedFunction(this.handleTransitionExited, this.props.onExited),
      children: content
    });
  }
  render() {
    let content = _jsx(Dialog, {
      ...omitProps(this.props, Overlay.allowedProps),
      ...pickProps(this.props, Dialog.allowedProps),
      defaultFocusElement: this.props.defaultFocusElement,
      open: this.state.open,
      elementRef: this.handleRef,
      children: this.props.children
    });
    if (this.props.transition) {
      content = this.renderTransition(content);
    }
    return _jsx(Portal, {
      ...pickProps(this.props, Portal.allowedProps),
      open: this.props.open || this.state.transitioning,
      onOpen: createChainedFunction(this.handlePortalOpen, this.props.onOpen),
      children: content
    });
  }
}, _Overlay.displayName = "Overlay", _Overlay.allowedProps = allowedProps, _Overlay.propTypes = propTypes, _Overlay.defaultProps = {
  open: false,
  insertAt: 'bottom',
  shouldContainFocus: false,
  shouldReturnFocus: false,
  shouldCloseOnDocumentClick: false,
  shouldCloseOnEscape: true,
  in: false,
  unmountOnExit: false,
  transitionOnMount: false,
  transitionEnter: true,
  transitionExit: true
}, _Overlay)) || _class);
export default Overlay;
export { Overlay };